/**
 * Supplier payments (Phase 2 W6-2; docs/plans/PHASE_2_W6.md §4). Shared by the API and the admin web,
 * so the page shows before saving exactly what the server will do:
 *
 * - a payment goes to the oldest open invoices first, unless the accountant chooses;
 * - money stays unallocated, a credit with the supplier, only when nothing is left open;
 * - an invoice's payment status is derived from what was allocated to it; the invoice row never
 *   changes for it.
 *
 * The database checks the same rules again (0036); these give the reasons in words before saving.
 */
import { z } from 'zod';

import { isUuidV7 } from '../ids/ids.ts';

export const PAYMENT_PHOTOS_MAX = 10;
export const PAYMENT_STATUSES = ['UNPAID', 'PARTIALLY_PAID', 'PAID'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

const dinars = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);

export const SupplierPaymentAllocationSchema = z.strictObject({
  invoiceId: z.uuid(),
  amount: dinars,
});
export type SupplierPaymentAllocation = z.infer<typeof SupplierPaymentAllocationSchema>;

export const SupplierPaymentSchema = z.strictObject({
  /** Chosen by the page: a retry with the same id changes nothing. */
  id: z.uuid().refine(isUuidV7, { message: 'UUIDv7 expected' }),
  supplierId: z.uuid(),
  boxId: z.uuid(),
  amount: dinars,
  note: z.string().trim().max(500).nullable().default(null),
  /** Null: the oldest open invoices first. */
  allocations: z
    .array(SupplierPaymentAllocationSchema)
    .max(200)
    .refine((list) => new Set(list.map((a) => a.invoiceId)).size === list.length, {
      message: 'An invoice appears once',
    })
    .nullable()
    .default(null),
  attachmentIds: z.array(z.uuid()).min(1).max(PAYMENT_PHOTOS_MAX),
});
export type SupplierPaymentInput = z.infer<typeof SupplierPaymentSchema>;

/** Derived, never stored. An invoice of 0 dinars has nothing to pay. */
export function invoicePaymentStatus(total: number, paid: number): PaymentStatus {
  if (paid >= total) return 'PAID';
  return paid > 0 ? 'PARTIALLY_PAID' : 'UNPAID';
}

export interface OpenInvoice {
  readonly id: string;
  readonly invoiceDate: string;
  /** Breaks a tie between invoices of the same date: the one recorded first is the older. */
  readonly recordedAt: string;
  readonly open: number;
}

export function oldestFirst<T extends OpenInvoice>(invoices: readonly T[]): T[] {
  return [...invoices].sort(
    (a, b) =>
      a.invoiceDate.localeCompare(b.invoiceDate) ||
      a.recordedAt.localeCompare(b.recordedAt) ||
      a.id.localeCompare(b.id),
  );
}

export interface PaymentSplit {
  readonly allocations: readonly SupplierPaymentAllocation[];
  /** What stays with the supplier as a credit. */
  readonly credit: number;
}

export function allocateOldestFirst(
  amount: number,
  invoices: readonly OpenInvoice[],
): PaymentSplit {
  let left = amount;
  const allocations: SupplierPaymentAllocation[] = [];
  for (const inv of oldestFirst(invoices)) {
    if (left === 0) break;
    const take = Math.min(left, inv.open);
    if (take > 0) {
      allocations.push({ invoiceId: inv.id, amount: take });
      left -= take;
    }
  }
  return { allocations, credit: left };
}

export type AllocationProblem =
  'unknown_invoice' | 'more_than_open' | 'more_than_payment' | 'credit_while_open';

/** Why the accountant's own split is refused, or null when it stands. */
export function checkAllocations(
  amount: number,
  invoices: readonly OpenInvoice[],
  chosen: readonly SupplierPaymentAllocation[],
): { readonly problem: AllocationProblem; readonly invoiceId?: string } | null {
  const open = new Map(invoices.map((i) => [i.id, i.open]));
  for (const a of chosen) {
    const available = open.get(a.invoiceId);
    if (available === undefined) return { problem: 'unknown_invoice', invoiceId: a.invoiceId };
    if (a.amount > available) return { problem: 'more_than_open', invoiceId: a.invoiceId };
  }
  const allocated = chosen.reduce((sum, a) => sum + a.amount, 0);
  if (allocated > amount) return { problem: 'more_than_payment' };
  const totalOpen = invoices.reduce((sum, i) => sum + i.open, 0);
  if (allocated < amount && allocated < totalOpen) return { problem: 'credit_while_open' };
  return null;
}
